import { useMemo } from 'react';

import { RegisterProvider, type UserInfo } from '../context/register.context';
import CGUScreen from './CGUScreen';
import CityScreen from './CityScreen';
import NamesScreen from './NamesScreen';
import PhoneScreen from './PhoneScreen';

export type FormStep = 'cgu' | 'names' | 'city' | 'phone';

/** Payload that each step can pass to onNext (CGU data, partial user info, or full UserInfo). */
export type FormStepNextPayload = { hasAcceptedCGU?: boolean; cguId?: string } | Partial<UserInfo>;

interface FormScreenProps {
  step: FormStep;
  onNext: (data?: FormStepNextPayload) => void | Promise<void>;
  onCancel: () => void;
  onBack: () => void;
  initialData?: Partial<UserInfo>;
  error?: string | null;
  isLoading?: boolean;
}

export default function FormScreen({
  step,
  onNext,
  onCancel,
  onBack,
  initialData,
  error,
  isLoading,
}: FormScreenProps) {
  const dummyOnEndForm = async () => {};

  const screen = useMemo(() => {
    switch (step) {
      case 'cgu':
        return <CGUScreen onCancel={onCancel} onBack={onBack} onNext={onNext} />;
      case 'names':
        return <NamesScreen onCancel={onCancel} onBack={onBack} onNext={onNext} />;
      case 'city':
        return <CityScreen onCancel={onCancel} onBack={onBack} onNext={onNext} />;
      case 'phone':
        return (
          <PhoneScreen
            onCancel={onCancel}
            onBack={onBack}
            onNext={onNext}
            error={error}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  }, [step, onCancel, onBack, onNext, error, isLoading]);

  return (
    <RegisterProvider onEndForm={dummyOnEndForm} initialData={initialData}>
      {screen}
    </RegisterProvider>
  );
}
